import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { EmptyState } from '@siliconx/shared/ui/state';
import { Package } from 'lucide-react';
import type { AssetFinancialsResponse, AssetFinancialsRow } from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { formatUsd, formatDate } from '../../lib/format';
import { FinancialsReportScreen } from '../../components/financials/FinancialsReportScreen';
import { CoverageStatCard } from '../../components/financials/CoverageStatCard';
import { OutlookBand } from '../../components/financials/OutlookBand';
import { MarketDemandTile } from '../../components/financials/MarketDemandTile';
import { ImportFromErpLink } from '../../components/financials/ImportFromErpLink';
import { qk } from '../../lib/queryKeys';

/**
 * SIL-573 (I2-D1, wayfinder-iter2-spec.md §5, §7-D1) — Asset Financials,
 * Finance > Financial Records. Per-asset financial table composed entirely
 * from existing data: `GET /api/client/financials/asset-financials`
 * (financials-report.ts's `loadAssetFinancialsPage`, joining
 * v_client_asset_book_value + asset_class + fmv_pricing_snapshot +
 * asset_financial). Honest-data gate: every column here traces to a real
 * DB column — there is no ERP method / useful-life column, because those
 * are covered by the Depreciation Schedule and Capitalization Policy
 * screens, not by data this endpoint has.
 *
 * Uses the same `FinancialsReportScreen` skeleton (PageHeader + QueryBoundary)
 * as book-vs-fmv.tsx, and the SAME `CoverageStatCard` that page already
 * mounts for the ERP-coverage stat — no new "coming soon" tiles, no
 * client-side recomputation of a stat the server already owns.
 */
export const Route = createFileRoute('/financials/asset-financials')({
  component: AssetFinancialsPage,
});

function AssetFinancialsPage() {
  const [cursor, setCursor] = useState<string | null>(null);
  const [rows, setRows] = useState<AssetFinancialsRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const pageQ = useQuery<AssetFinancialsResponse>({
    queryKey: qk.financials.assetFinancials(cursor),
    queryFn: async () => {
      const res = await api.api.client.financials['asset-financials'].$get({
        query: { limit: '50', ...(cursor ? { cursor } : {}) },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const page = (await res.json()) as unknown as AssetFinancialsResponse;
      setRows((prev) => (cursor === null ? page.assets : [...prev, ...page.assets]));
      setNextCursor(page.pageMeta.nextCursor);
      return page;
    },
  });

  return (
    <FinancialsReportScreen
      title="Asset Financials"
      pinTo="/financials/asset-financials"
      subtitle="View acquisition cost, book value, depreciation to date, current FMV, and ERP match status by asset."
      query={pageQ}
    >
      {() => (
        <div className="flex flex-col gap-5">
          <CoverageStatCard />

          {/* SIL-573 PR 1 of 2 — Outlook band. Own query, own coverage gate;
           * never wired into this screen's export path (none exists here —
           * OutlookBand.tsx's header comment). */}
          <OutlookBand />

          {/* SIL-573 PR 2 of 2 — market-demand tile, last element of the
           * Outlook band region. Own query, own component (MarketDemandTile.tsx
           * header) — "demand for what you hold," not the /opportunities feed;
           * never wired into this screen's export path either. */}
          <MarketDemandTile />

          {rows.length === 0 ? (
            <EmptyState
              icon={Package}
              title="No financial data yet"
              description="Record purchase price and in-service dates on your assets to see per-asset financials here."
              action={<ImportFromErpLink />}
            />
          ) : (
            <Card>
              <PanelTitle
                title="By asset"
                description="Acquisition cost, book value, depreciation, current FMV, and ERP match status"
              />
              <div className="mt-4 overflow-x-auto">
                <AssetFinancialsTable rows={rows} />
              </div>
              {nextCursor && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setCursor(nextCursor)}
                    disabled={pageQ.isFetching}
                    className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                  >
                    {pageQ.isFetching ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </FinancialsReportScreen>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

const ERP_MATCH_LABEL: Record<'manual' | 'csv' | 'erp', string> = {
  manual: 'Manual',
  csv: 'CSV import',
  erp: 'ERP match',
};

const ERP_MATCH_STYLE: Record<'manual' | 'csv' | 'erp', string> = {
  manual: 'bg-muted text-muted-foreground ring-border',
  csv: 'bg-brand-50 text-brand-700 ring-brand-200',
  erp: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
};

function ErpMatchChip({ status }: { status: AssetFinancialsRow['erpMatchStatus'] }) {
  if (status === null) {
    return (
      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-inset ring-border">
        No data
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${ERP_MATCH_STYLE[status]}`}
    >
      {ERP_MATCH_LABEL[status]}
    </span>
  );
}

function DeltaCell({ value }: { value: number | null }) {
  if (value === null) {
    return <span className="tabular-nums text-muted-foreground">—</span>;
  }
  return (
    <span className={`tabular-nums ${value >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
      {value >= 0 ? '+' : ''}
      {formatUsd(value)}
    </span>
  );
}

function AssetFinancialsTable({ rows }: { rows: AssetFinancialsRow[] }) {
  return (
    <table className="w-full min-w-[1000px] text-sm">
      <thead>
        <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <th className="py-2 pr-4">Asset</th>
          <th className="py-2 pr-4">Class</th>
          <th className="py-2 pr-4 text-right">Acquisition cost</th>
          <th className="py-2 pr-4">Acquisition date</th>
          <th className="py-2 pr-4 text-right">Book value</th>
          <th className="py-2 pr-4 text-right">Depreciation to date</th>
          <th className="py-2 pr-4 text-right">Current FMV (low–high)</th>
          <th className="py-2 pr-4 text-right">Book vs FMV</th>
          <th className="py-2 pr-4">ERP match</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((r) => (
          <tr key={r.assetId}>
            <td className="py-3 pr-4 align-middle">
              <Link
                to="/assets/$assetId"
                params={{ assetId: r.assetId }}
                className="font-mono text-xs text-brand-600 hover:underline"
              >
                {r.serialNumber ?? r.assetId.slice(0, 8)}
              </Link>
            </td>
            <td className="py-3 pr-4 align-middle text-foreground">{r.assetClassName ?? r.assetClassId}</td>
            <td className="py-3 pr-4 align-middle text-right tabular-nums text-foreground">
              {formatUsd(r.acquisitionCostUsd)}
            </td>
            <td className="py-3 pr-4 align-middle text-muted-foreground">{formatDate(r.acquisitionDate)}</td>
            <td className="py-3 pr-4 align-middle text-right">
              <span className="inline-flex items-center gap-1 tabular-nums text-foreground">
                {formatUsd(r.bookValueUsd)}
                {r.isEstimate && (
                  <span
                    title="Estimated — falls back to SKU launch price where purchase price is unknown"
                    className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground"
                  >
                    est.
                  </span>
                )}
              </span>
            </td>
            <td className="py-3 pr-4 align-middle text-right tabular-nums text-foreground">
              {formatUsd(r.depreciationToDateUsd)}
            </td>
            <td className="py-3 pr-4 align-middle text-right tabular-nums text-foreground">
              {r.fmvLowUsd === null && r.fmvHighUsd === null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <span>
                  {formatUsd(r.fmvLowUsd)}–{formatUsd(r.fmvHighUsd)}
                  <span className="ml-1 text-xs text-muted-foreground">(med {formatUsd(r.fmvMedUsd)})</span>
                </span>
              )}
            </td>
            <td className="py-3 pr-4 align-middle text-right">
              <DeltaCell value={r.bookVsFmvDeltaUsd} />
            </td>
            <td className="py-3 pr-4 align-middle">
              <ErpMatchChip status={r.erpMatchStatus} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
